// src/components/tokenPage/risk/UpsideDownsideDeviationCard.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { useTokenPrice } from "@/contexts/token";
import { rollingUpsideDeviation, rollingDownsideDeviation, toReturns, TRADING_DAYS } from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';

const RANGE_TO_DAYS: Record<Range, number> = { '90d': 90, '180d': 180, '365d': 365, 'All': Infinity };

const UpsideDownsideDeviationCard: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const [win, setWin] = useState<30|90|180|365>(90);
  
  const { token, isLoading } = useTokenPrice();
  const prices = token.prices;

  const { dates, up, down } = useMemo(() => {
    const d = prices.dates;
    const values = prices.values;
    
    if (d.length === 0 || values.length === 0) {
      return { dates: [], up: [], down: [] };
    }
    
    const rets = toReturns(values);
    const minPoints = win === 30 ? 20 : win === 90 ? 60 : win === 180 ? 120 : 180;
    
    const upDev = rollingUpsideDeviation(rets, win, 0, minPoints, TRADING_DAYS.CRYPTO);
    const downDev = rollingDownsideDeviation(rets, win, 0, minPoints, TRADING_DAYS.CRYPTO);
    
    // Convert to percentage
    const upPct = upDev.map(v => v !== null ? v * 100 : null);
    const downPct = downDev.map(v => v !== null ? v * 100 : null);
    
    const maxPoints = RANGE_TO_DAYS[range];
    const start = maxPoints === Infinity ? 0 : Math.max(0, d.length - maxPoints);
    const datesAligned = d.slice(start);
    const metricStart = Math.max(0, start - 1);
    const upAligned = upPct.slice(metricStart, metricStart + datesAligned.length);
    const downAligned = downPct.slice(metricStart, metricStart + datesAligned.length);
    
    // Find first index with valid data in either series
    const upFirst = upAligned.findIndex(v => v !== null && Number.isFinite(v));
    const downFirst = downAligned.findIndex(v => v !== null && Number.isFinite(v));
    const validIndices = [upFirst, downFirst].filter(idx => idx >= 0);
    const firstValidIdx = validIndices.length > 0 ? Math.min(...validIndices) : -1;
    
    if (firstValidIdx >= 0) {
      return { 
        dates: datesAligned.slice(firstValidIdx), 
        up: upAligned.slice(firstValidIdx), 
        down: downAligned.slice(firstValidIdx) 
      };
    }
    return { dates: datesAligned, up: upAligned, down: downAligned };
  }, [prices, range, win]);

  // Hide if no data available (after loading)
  const hasData = up.some(v => v !== null && Number.isFinite(v)) || 
                  down.some(v => v !== null && Number.isFinite(v));
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Upside vs Downside Deviation"
      subtitle="Annualized deviations"
      icon="trending-down"
      iconColor="text-rose-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400 mr-1">Window</span>
          {[30, 90, 180, 365].map(w => (
            <button 
              key={w}
              className={`text-xs px-2 py-1 rounded-md ${win===w?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} 
              onClick={() => setWin(w as 30|90|180|365)}
            >
              {w}d
            </button>
          ))}
          <span className="text-[11px] text-slate-500 mx-1">|</span>
          <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
        </div>
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[
          { name: `Upside Deviation (${win}d)`, values: up, color: '#10b981', areaStyle: true },
          { name: `Downside Deviation (${win}d)`, values: down, color: '#ef4444', areaStyle: true },
        ]}
        height={400}
        yAxisFormatter={(v) => `${v.toFixed(1)}%`}
        tooltipFormatter={(params) => {
          const items = params as Array<{ axisValue: string; seriesName: string; value: number; marker: string }>;
          if (!items || items.length === 0) return '';
          const dateStr = items[0]?.axisValue || '';
          const formattedDate = formatDateLabel(dateStr, 'medium');
          let tooltip = `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${formattedDate}</div>`;
          items.forEach((item) => {
            tooltip += `${item.marker} ${item.seriesName}: <span style="color:#f3f4f6;">${Number(item.value).toFixed(1)}%</span><br/>`;
          });
          return tooltip;
        }}
      />
    </DataCard>
  );
};

export default UpsideDownsideDeviationCard;
